/**
 * Make yanamas yours — end-user customization of the diary theme.
 *
 * The owner's theme-lab picks (Gochi Hand, Quicksand, hand-drawn buttons,
 * index cards) are the defaults; this panel lets each person restyle those
 * four axes for themselves. Choices persist per-device via ThemeProvider's
 * localStorage keys, the same way every other diary preference does.
 *
 * Renders nothing outside diary mode: customizing a theme you can't see
 * would be noise, and modern mode must stay untouched.
 */
import React, { useState } from 'react'
import { ChevronDown, ChevronUp, Palette, RotateCcw } from 'lucide-react'
import {
  useTheme,
  DIARY_FONTS,
  UI_FONTS,
  YANAMAS_BUTTON_STYLES,
  YANAMAS_CARD_STYLES,
  BODY_FONTS,
  DEFAULT_DIARY_FONT,
  DEFAULT_YANAMAS_BUTTON,
  DEFAULT_YANAMAS_CARD,
  DEFAULT_YANAMAS_UI_FONT,
  DEFAULT_YANAMAS_BODY_FONT,
} from '@/components/providers/ThemeProvider'
import type { YanamasButtonStyle, YanamasCardStyle } from '@/components/providers/ThemeProvider'

/** The diary-mode UI sans choices: the rounder faces, plus the classics. */
const UI_CHOICES = ['quicksand', 'nunito', 'comfortaa', 'baloo-2', 'inter'] as const

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "'Courier New', monospace",
        fontSize: 10,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        color: 'var(--ink-faded)',
      }}
    >
      {children}
    </div>
  )
}

/** A selectable swatch; selected = sepia ink frame, like a circled choice. */
function Choice({
  selected,
  onClick,
  title,
  children,
}: {
  selected: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={selected}
      className="text-left rounded-md px-2.5 py-1.5 transition-all"
      style={{
        border: selected ? '2px solid var(--ink-sepia)' : '1px dashed var(--ink-faded)',
        background: selected ? 'var(--paper-cream)' : 'transparent',
        boxShadow: selected ? '1px 2px 5px var(--shadow-color)' : 'none',
        color: 'var(--ink-black)',
      }}
    >
      {children}
    </button>
  )
}

/** Miniature live preview of one button style. */
function ButtonPreview({ styleId }: { styleId: YanamasButtonStyle }) {
  const base: React.CSSProperties = {
    display: 'inline-block',
    fontSize: 12,
    padding: '3px 10px',
    fontFamily: 'var(--font-ui)',
    pointerEvents: 'none',
  }
  switch (styleId) {
    case 'sketch':
      return <span style={{ ...base, border: '2px solid var(--ink-black)', borderRadius: '10px 4px 12px 5px', fontFamily: 'var(--font-handwritten)', fontSize: 14 }}>Save</span>
    case 'wax':
      return <span style={{ ...base, background: 'var(--horton-purple)', color: '#FDF8F3', borderRadius: 12, fontWeight: 700 }}>Save</span>
    case 'ink':
      return <span style={{ ...base, background: 'var(--ink-sepia)', color: 'var(--paper-cream)', borderRadius: 4 }}>Save</span>
    case 'tape':
      return <span style={{ ...base, background: 'var(--washi-tape-pink)', color: 'var(--ink-sepia)', transform: 'rotate(-1.5deg)', backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)' }}>Save</span>
  }
}

/** Miniature preview of one card style. */
function CardPreview({ styleId }: { styleId: YanamasCardStyle }) {
  const base: React.CSSProperties = {
    width: 74,
    height: 40,
    borderRadius: 3,
    background: 'var(--paper-cream)',
    border: '1px solid var(--paper-margin)',
    position: 'relative',
    overflow: styleId === 'taped' ? 'visible' : 'hidden',
    pointerEvents: 'none',
  }
  const deco: Record<YanamasCardStyle, React.CSSProperties> = {
    index: { backgroundImage: 'linear-gradient(90deg, transparent 12px, color-mix(in srgb, var(--bookmark-red) 45%, transparent) 12px, color-mix(in srgb, var(--bookmark-red) 45%, transparent) 13px, transparent 13px)' },
    plain: { borderLeft: '3px solid var(--ink-sepia)' },
    ruled: { backgroundImage: 'repeating-linear-gradient(transparent 0 9px, var(--paper-margin) 9px 10px)', borderLeft: '2px solid var(--bookmark-red)' },
    taped: {},
    folded: {},
  }
  return (
    <span style={{ ...base, ...deco[styleId] }}>
      {styleId === 'taped' && (
        <span style={{ position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%) rotate(-2deg)', width: 26, height: 8, background: 'rgba(255,243,205,0.95)', boxShadow: '0 1px 1px rgba(0,0,0,0.1)' }} />
      )}
      {styleId === 'folded' && (
        <span style={{ position: 'absolute', top: 0, right: 0, borderWidth: '0 10px 10px 0', borderStyle: 'solid', borderColor: 'var(--paper-aged) var(--paper-warm)' }} />
      )}
    </span>
  )
}

export const YanamasCustomizer: React.FC<{ className?: string }> = ({ className = '' }) => {
  const {
    isDiary,
    diaryFont, setDiaryFont,
    yanamasUiFont, setYanamasUiFont,
    yanamasBodyFont, setYanamasBodyFont,
    yanamasButtonStyle, setYanamasButtonStyle,
    yanamasCardStyle, setYanamasCardStyle,
  } = useTheme()
  const [open, setOpen] = useState(false)

  if (!isDiary) return null

  const isDefault =
    diaryFont === DEFAULT_DIARY_FONT &&
    yanamasUiFont === DEFAULT_YANAMAS_UI_FONT &&
    yanamasBodyFont === DEFAULT_YANAMAS_BODY_FONT &&
    yanamasButtonStyle === DEFAULT_YANAMAS_BUTTON &&
    yanamasCardStyle === DEFAULT_YANAMAS_CARD

  const resetAll = () => {
    setDiaryFont(DEFAULT_DIARY_FONT)
    setYanamasUiFont(DEFAULT_YANAMAS_UI_FONT)
    setYanamasBodyFont(DEFAULT_YANAMAS_BODY_FONT)
    setYanamasButtonStyle(DEFAULT_YANAMAS_BUTTON)
    setYanamasCardStyle(DEFAULT_YANAMAS_CARD)
  }

  return (
    <div
      className={`rounded-lg ${className}`}
      style={{ background: 'var(--paper-aged)', border: '1px dashed var(--ink-faded)' }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2"
        style={{ color: 'var(--ink-sepia)' }}
      >
        <Palette className="w-4 h-4 shrink-0" />
        <span style={{ fontFamily: 'var(--font-handwritten)', fontSize: 17 }}>
          Make it yours
        </span>
        <span
          className="text-xs"
          style={{ fontFamily: 'var(--font-body)', fontStyle: 'italic', color: 'var(--ink-faded)' }}
        >
          — handwriting, buttons, cards
        </span>
        {!isDefault && (
          <span
            className="text-app-nano rounded-full px-2 py-0.5"
            title="You've customized the look — open to see or reset"
            style={{
              fontFamily: "'Courier New', monospace",
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: 'var(--paper-cream)',
              background: 'var(--bookmark-red)',
            }}
          >
            customized
          </span>
        )}
        <span className="ml-auto">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-4">
          {/* Handwriting */}
          <div className="space-y-1.5">
            <SectionLabel>Your handwriting</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {DIARY_FONTS.map((f) => (
                <Choice
                  key={f.id}
                  selected={diaryFont === f.id}
                  onClick={() => setDiaryFont(f.id)}
                  title={f.platformNotes}
                >
                  <span style={{ fontFamily: f.fontFamily, fontSize: 17, lineHeight: 1.1 }}>{f.name}</span>
                  <span className="block text-app-nano" style={{ color: 'var(--ink-faded)', fontFamily: 'var(--font-ui)' }}>
                    {f.description}
                  </span>
                </Choice>
              ))}
            </div>
          </div>

          {/* UI font */}
          <div className="space-y-1.5">
            <SectionLabel>Buttons &amp; labels font</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {UI_CHOICES.map((id) => {
                const f = UI_FONTS.find((u) => u.id === id)!
                return (
                  <Choice key={id} selected={yanamasUiFont === id} onClick={() => setYanamasUiFont(id)}>
                    <span style={{ fontFamily: f.fontFamily, fontSize: 13, fontWeight: 600 }}>{f.name}</span>
                  </Choice>
                )
              })}
            </div>
          </div>

          {/* Body / quiet-text font */}
          <div className="space-y-1.5">
            <SectionLabel>Quiet text font</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {BODY_FONTS.map((f) => (
                <Choice key={f.id} selected={yanamasBodyFont === f.id} onClick={() => setYanamasBodyFont(f.id)}>
                  <span style={{ fontFamily: f.fontFamily, fontSize: 14, fontStyle: 'italic' }}>{f.name}</span>
                </Choice>
              ))}
            </div>
          </div>

          {/* Button style */}
          <div className="space-y-1.5">
            <SectionLabel>Buttons look like</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {YANAMAS_BUTTON_STYLES.map((o) => (
                <Choice
                  key={o.id}
                  selected={yanamasButtonStyle === o.id}
                  onClick={() => setYanamasButtonStyle(o.id)}
                  title={o.description}
                >
                  <span className="flex items-center gap-2">
                    <ButtonPreview styleId={o.id} />
                    <span className="text-xs" style={{ fontFamily: 'var(--font-ui)' }}>{o.name}</span>
                  </span>
                </Choice>
              ))}
            </div>
          </div>

          {/* Card style */}
          <div className="space-y-1.5">
            <SectionLabel>Cards look like</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {YANAMAS_CARD_STYLES.map((o) => (
                <Choice
                  key={o.id}
                  selected={yanamasCardStyle === o.id}
                  onClick={() => setYanamasCardStyle(o.id)}
                  title={o.description}
                >
                  <span className="flex items-center gap-2">
                    <CardPreview styleId={o.id} />
                    <span className="text-xs" style={{ fontFamily: 'var(--font-ui)' }}>{o.name}</span>
                  </span>
                </Choice>
              ))}
            </div>
          </div>

          {!isDefault && (
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1"
              style={{
                fontFamily: 'var(--font-ui)',
                color: 'var(--paper-cream)',
                background: 'var(--ink-faded)',
              }}
            >
              <RotateCcw className="w-3 h-3" />
              Back to the Journal look
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default YanamasCustomizer
